import { i18n, requestPlatform } from '@tencent/vb-web-base';
import { to } from '@tencent/vb-web-base-core';
import { NetworkError } from '@common/lib/error';
import {
  InviteCodeMerchantActInfoResp,
  InviteCodePersonalActInfoResp,
  IActivityListData,
  IGetActivityRecordReq,
  IActivityRecordData,
  IAwardStock,
  IJoinActivityReq,
  IRecordListData,
  IMobileRegisterReq,
} from '../interface/IModel';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import { NOT_LOGIN, ProductInfoMap } from '@common/core/help/constant';
import { IuseAwardReq, ISendAuthCodeReq, IRegisterNameListReq, IRegisterNameListSimpleReq, IRegisterNameListResp } from '@loan/vb-node-loan/types';
import api from '@loan/vb-node-loan/types/apiPlatform';

dayjs.extend(isBetween);

/**
 * 获取用户状态
 */
export async function GetUserStatus() {
  const { getT } = i18n;
  const $t = getT();
  const [respData, err] = await to(
    api.node.requestPlatformNodeGetUserStatus(),
  );
  if (err) {
    throw new NetworkError({ code: err.status, message: $t('获取用户信息异常'), tag: 'getUserStatusError' });
  }
  if (respData.code !== 0) {
    throw new NetworkError({ code: respData.code, message: respData.message, tag: 'getUserStatusError' });
  }

  return respData.data;
}

// 获取商户邀请码活动配置
export async function GetInviteCodeMerchantActInfo(key: string): Promise<InviteCodeMerchantActInfoResp> {
  const { getT } = i18n;
  const $t = getT();
  const [respData, err] = await to(
    requestPlatform<{
      code: number;
      message: string;
      data: InviteCodeMerchantActInfoResp;
      serverTime: number;
    }>('/node/act/getConfig', 'GET', { key }),
  );
  if (err) {
    throw new NetworkError({
      code: err.status,
      message: $t('系统繁忙，请稍后重试'),
      tag: 'getInviteCodeMerchantActInfoError',
    });
  }
  const { code, message, data, serverTime } = respData;
  if (code !== 0) {
    throw new NetworkError({ code, message, tag: 'getInviteCodeMerchantActInfoError' });
  }
  const now = dayjs.unix(serverTime);
  const start = dayjs(data.beginDateTime);
  const end = dayjs(data.endDateTime);
  if (!now.isBetween(start, end)) {
    data.isOutdate = true;
  }
  return data;
}

// 获取个人邀请码活动配置
export async function GetInviteCodePersonalActInfo(key: string): Promise<InviteCodePersonalActInfoResp | null> {
  const { getT } = i18n;
  const $t = getT();
  const [respData, err] = await to(
    requestPlatform<{
      code: number;
      message: string;
      data: InviteCodePersonalActInfoResp;
      serverTime: number;
    }>('/node/act/getConfig', 'GET', { key }),
  );
  if (err) {
    throw new NetworkError({
      code: err.status,
      message: $t('系统繁忙，请稍后重试'),
      tag: 'getInviteCodePersonalActInfoError',
    });
  }
  const { code, message, data } = respData;
  if (code !== 0) {
    throw new NetworkError({ code, message, tag: 'getInviteCodePersonalActInfoError' });
  }
  return data;
}

// 生成个人邀请码
export async function GenerateInviteCode(activityCode: string): Promise<{ invitation_code: string }> {
  const { getT } = i18n;
  const $t = getT();
  const [respData, err] = await to(
    requestPlatform<{
      code: number;
      message: string;
      data: {
        invitation_code: string;
      };
    }>('/node/loan/act/generateInviteCode', 'GET', { activity_code: activityCode }),
  );
  if (err !== null) {
    if (err.status === NOT_LOGIN) {
      throw new NetworkError({ code: err.status, message: $t('系统繁忙，请稍后重试'), tag: 'noLogin' });
    } else {
      throw new NetworkError({ code: err.status, message: $t('系统繁忙，请稍后重试'), tag: 'generateInviteCodeError' });
    }
  }
  const { code, message, data } = respData;
  if (code !== 0) {
    throw new NetworkError({ code, message, tag: 'generateInviteCodeError' });
  }
  return data;
}

// 查询邀请奖励金额
export async function QuerySpecificInfo(): Promise<{ awardAmt: string }> {
  const { getT } = i18n;
  const $t = getT();
  const [respData, err] = await to(
    requestPlatform<{
      code: number;
      message: string;
      data: {
        awardAmt: string;
      };
    }>('/node/loan/act/querySpecificInfo'),
  );
  if (err !== null) {
    if (err.status === NOT_LOGIN) {
      throw new NetworkError({ code: err.status, message: $t('系统繁忙，请稍后重试'), tag: 'noLogin' });
    } else {
      throw new NetworkError({ code: err.status, message: $t('系统繁忙，请稍后重试'), tag: 'querySpecificInfoError' });
    }
  }
  const { code, message, data } = respData;
  if (code !== 0) {
    throw new NetworkError({ code, message, tag: 'querySpecificInfoError' });
  }
  return data;
}

// 查询是否有活动资格
export async function CheckActivityRule(inviteCode: string): Promise<{ pass_mark: number }> {
  const { getT } = i18n;
  const $t = getT();
  const [respData, err] = await to(
    requestPlatform<{
      code: number;
      message: string;
      data: {
        pass_mark: number;
      };
    }>('/node/loan/act/checkActivityRule', 'GET', { productNo: ProductInfoMap.DEFAULT, inviteCode }),
  );
  if (err !== null) {
    if (err.status === NOT_LOGIN) {
      throw new NetworkError({ code: err.status, message: $t('系统繁忙，请稍后重试'), tag: 'noLogin' });
    } else {
      throw new NetworkError({ code: err.status, message: $t('系统繁忙，请稍后重试'), tag: 'checkActivityRuleError' });
    }
  }
  const { code, message, data } = respData;
  if (code !== 0) {
    throw new NetworkError({ code, message, tag: 'checkActivityRuleError' });
  }
  return data;
}

// 获取贷款业务配置
export async function GetLoanConfigList<T>(key: string, filePath?: string): Promise<T> {
  const { getT } = i18n;
  const $t = getT();
  const [respData, err] = await to(
    requestPlatform<{
      code: number;
      message: string;
      data: T;
    }>('/node/loan/loanConfig/queryConfigList', 'GET', { key, filePath }),
  );
  if (err) {
    throw new NetworkError({
      code: err.status,
      message: $t('系统繁忙，请稍后重试'),
      tag: 'queryConfigListError',
    });
  }
  const { code, message, data } = respData;
  if (code !== 0) {
    throw new NetworkError({ code, message, tag: 'queryConfigListError' });
  }
  return data;
}

// 获取车主专区首页配置
export async function GetRightHomeConfig<T>(params: {brand: string; channel?: string}): Promise<T> {
  const { getT } = i18n;
  const $t = getT();
  const [respData, err] = await to(
    requestPlatform<{
      code: number;
      message: string;
      data: T;
    }>('/node/loan/carOwnerRight/getHomeConfig', 'GET', params),
  );
  if (err) {
    throw new NetworkError({
      code: err.status,
      message: $t('系统繁忙，请稍后重试'),
      tag: 'getRightHomeConfigError',
    });
  }
  const { code, message, data } = respData;
  if (code !== 0) {
    throw new NetworkError({ code, message, tag: 'getRightHomeConfigError' });
  }
  return data;
}

// 获取营销活动列表
export async function GetActivityList(activityNo: string): Promise<IActivityListData> {
  const { getT } = i18n;
  const $t = getT();
  const [respData, err] = await to(
    requestPlatform<{
      code: number;
      message: string;
      data: IActivityListData;
    }>('/node/loan/carOwnerRight/getActivityList', 'GET', { activityNo }),
  );
  if (err) {
    throw new NetworkError({
      code: err.status,
      message: $t('系统繁忙，请稍后重试'),
      tag: 'getActivityListError',
    });
  }
  const { code, message, data } = respData;
  if (code !== 0) {
    throw new NetworkError({ code, message, tag: 'getActivityListError' });
  }
  return data;
}

// 获取营销活动参与记录
export async function GetActivityRecord(params: IGetActivityRecordReq): Promise<IActivityRecordData> {
  const { getT } = i18n;
  const $t = getT();
  const [respData, err] = await to(
    requestPlatform<{
      code: number;
      message: string;
      data: IActivityRecordData;
    }>('/node/loan/carOwnerRight/getActivityRecoed', 'POST', params),
  );
  if (err) {
    throw new NetworkError({
      code: err.status,
      message: $t('系统繁忙，请稍后重试'),
      tag: 'getActivityRecordError',
    });
  }
  const { code, message, data } = respData;
  if (code !== 0) {
    throw new NetworkError({ code, message, tag: 'getActivityRecordError' });
  }
  return data;
}

// 获取营销活动奖品库存情况
export async function CheckAwardStock(awardConfId: string): Promise<IAwardStock> {
  const { getT } = i18n;
  const $t = getT();
  const [respData, err] = await to(
    requestPlatform<{
      code: number;
      message: string;
      data: IAwardStock;
    }>('/node/loan/carOwnerRight/checkAwardStock', 'GET', { awardConfId }),
  );
  if (err) {
    throw new NetworkError({
      code: err.status,
      message: $t('系统繁忙，请稍后重试'),
      tag: 'checkAwardStockError',
    });
  }
  const { code, message, data } = respData;
  if (code !== 0) {
    throw new NetworkError({ code, message, tag: 'checkAwardStockError' });
  }
  return data;
}

// 参与活动
export async function JoinActivity(params: IJoinActivityReq): Promise<null> {
  const { getT } = i18n;
  const $t = getT();
  const [respData, err] = await to(
    requestPlatform<{
      code: number;
      message: string;
      data: null;
    }>('/node/loan/carOwnerRight/joinActivity', 'POST', params),
  );
  if (err) {
    throw new NetworkError({
      code: err.status,
      message: $t('系统繁忙，请稍后重试'),
      tag: 'joinActivityError',
    });
  }
  const { code, message, data } = respData;
  if (code === 1611059071) {
    return data;
  }
  if (code !== 0) {
    throw new NetworkError({ code, message, tag: 'joinActivityError' });
  }
  return data;
}

// 获取权益领取记录
export async function GetRecordList(activityNoList: string[]): Promise<IRecordListData> {
  const { getT } = i18n;
  const $t = getT();
  const [respData, err] = await to(
    requestPlatform<{
      code: number;
      message: string;
      data: IRecordListData;
    }>('/node/loan/carOwnerRight/getRecordList', 'POST', { activityNoList }),
  );
  if (err) {
    throw new NetworkError({
      code: err.status,
      message: $t('系统繁忙，请稍后重试'),
      tag: 'getRecordListError',
    });
  }
  const { code, message, data } = respData;
  if (code !== 0) {
    throw new NetworkError({ code, message, tag: 'getRecordListError' });
  }
  return data;
}

// 修改奖品使用状态
export async function UseAward(params: IuseAwardReq): Promise<null> {
  const { getT } = i18n;
  const $t = getT();
  const [respData, err] = await to(
    requestPlatform<{
      code: number;
      message: string;
      data: null;
    }>('/node/loan/carOwnerRight/useAward', 'POST', params),
  );
  if (err) {
    throw new NetworkError({
      code: err.status,
      message: $t('系统繁忙，请稍后重试'),
      tag: 'useAwardError',
    });
  }
  const { code, message, data } = respData;
  if (code !== 0) {
    throw new NetworkError({ code, message, tag: 'useAwardError' });
  }
  return data;
}

// 车主权益页面手机号登记
export async function RegisterMobile(
  params: IMobileRegisterReq,
): Promise<'register' | 'suc' | 'registered-tesla' | 'registered-notesla'> {
  const { getT } = i18n;
  const $t = getT();
  const [respData, err] = await to(
    requestPlatform<{
      code: number;
      message: string;
      data: { whitelistType?: number };
    }>('/node/loan/carOwnerRight/registerMobile', 'POST', params),
  );
  if (err) {
    throw new NetworkError({
      code: err.status,
      message: $t('系统繁忙，请稍后重试'),
      tag: 'registerMobileError',
    });
  }
  const { code, message, data } = respData;
  let status: 'register' | 'suc' | 'registered-tesla' | 'registered-notesla' = 'suc';
  if (code !== 0) {
    if (code === 1611841167) {
      if (data.whitelistType === 1) {
        status = 'registered-tesla';
      } else if (data.whitelistType === 2) {
        status = 'registered-notesla';
      }
    } else {
      throw new NetworkError({ code, message, tag: 'registerMobileError' });
    }
  }
  return status;
}

// 检查是否tesla白名单
export async function CheckTeslaWhiteAndOwnerInfo(): Promise<{ isInTeslaWhite: boolean; rnNo: string; vehicleNo: string; barndId: string; }> {
  const { getT } = i18n;
  const $t = getT();
  const [respData, err] = await to(
    requestPlatform<{
      code: number;
      message: string;
      data: { isInTeslaWhite: boolean; rnNo: string; vehicleNo: string; barndId: string; };
    }>('/node/loan/carOwner/checkTeslaWhiteAndOwnerInfo', 'GET'),
  );
  if (err) {
    throw new NetworkError({
      code: err.status,
      message: $t('系统繁忙，请稍后重试'),
      tag: 'checkTeslaWhiteAndOwnerInfoError',
    });
  }
  const { code, message, data } = respData;
  if (code !== 0) {
    throw new NetworkError({ code, message, tag: 'checkTeslaWhiteAndOwnerInfoError' });
  }
  return data;
}

// 获取验证码
export async function SendAuthCode(params: ISendAuthCodeReq) {
  const { getT } = i18n;
  const $t = getT();
  const [respData, err] = await to(
    requestPlatform<{
      code: number;
      message: string;
      data: null
    }>('/node/loan/act/sendAuthCode', 'POST', params),
  );
  if (err) {
    throw new NetworkError({
      code: err.status,
      message: $t('系统繁忙，请稍后重试'),
      tag: 'sendAuthCodeError',
    });
  }
  const { code, message, data } = respData;
  if (code !== 0) {
    throw new NetworkError({ code, message, tag: 'sendAuthCodeError' });
  }
  return data;
}

// 通用预登记
export async function RegisterNameList(params: IRegisterNameListReq): Promise<IRegisterNameListResp> {
  const { getT } = i18n;
  const lang = i18n.getLang();
  const $t = getT();
  const [respData, err] = await to(
    requestPlatform<{
      code: number;
      message: string;
      data: IRegisterNameListResp
    }>('/node/loan/act/registerNameList', 'POST', params),
  );
  if (err) {
    throw new NetworkError({
      code: err.status,
      message: $t('系统繁忙，请稍后重试'),
      tag: 'registerNameListError',
    });
  }
  const { code, data } = respData;
  if (code !== 0) {
    const messageKey = lang.split('-').join('');
    const messageList = JSON.parse(data.not_pass_message_list[0] || '{}');
    const message = messageList[messageKey];
    throw new NetworkError({ code, message: message || respData.message, tag: 'registerNameListError' });
  }
  return data;
}

// 通用预登记(不用校验验证码)
export async function RegisterNameListSimple(params: IRegisterNameListSimpleReq): Promise<IRegisterNameListResp> {
  const { getT } = i18n;
  const lang = i18n.getLang();
  const $t = getT();
  const [respData, err] = await to(
    requestPlatform<{
      code: number;
      message: string;
      data: IRegisterNameListResp
    }>('/node/loan/act/registerNameListSimple', 'POST', params),
  );
  if (err) {
    throw new NetworkError({
      code: err.status,
      message: $t('系统繁忙，请稍后重试'),
      tag: 'registerNameListSimpleError',
    });
  }
  const { code, data } = respData;
  if (code !== 0) {
    const messageKey = lang.split('-').join('');
    const messageList = JSON.parse(data.not_pass_message_list[0] || '{}');
    const message = messageList[messageKey];
    throw new NetworkError({ code, message: message || respData.message, tag: 'registerNameListSimpleError' });
  }
  return data;
}
